import type { ReactElement } from 'react';
import { useMemo, useCallback } from 'react';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams } from '@/components/DataTable';
import type { PersonalInstalledToolListItem } from '@kirin/api-client/contracts/account-shadow-ai-installed-tools';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SHADOW_AI_REFRESH_INTERVAL } from '@/hooks/api/useShadowAiTools';
import { queryKeys } from '@/hooks/api/queryKeys';
import { createPersonalInstalledToolsColumns } from './columns';
import {
  fetchPersonalInstalledTools,
  type PersonalInstalledToolsMeta,
} from './api';
import {
  getInstalledToolRowId,
  SHADOW_AI_TABLE_SCROLL_CLASS,
} from '@/components/shared/shadow-ai';
import { ExpandedToolRow } from './ExpandedToolRow';
import { PERSONAL_INSTALLED_TOOLS_TABLE_CONFIG } from './constants';

function getQueryKeysFromParams(params: DataFetchParams) {
  return queryKeys.shadowAi.personalInstalledAiToolsDataTable({
    page: params.pagination.pageIndex + 1,
    limit: params.pagination.pageSize,
    filters: params.filters,
    sortBy: params.sort?.field,
    sortOrder: params.sort?.order,
    search: params.search,
  });
}

interface DataTablePersonalInstalledToolsContentProps {
  onMeta?: (meta: PersonalInstalledToolsMeta) => void;
}

export function DataTablePersonalInstalledToolsContent({
  onMeta,
}: DataTablePersonalInstalledToolsContentProps): ReactElement {
  const columns = useMemo(() => createPersonalInstalledToolsColumns(), []);

  const queryFn = useCallback(
    (params: DataFetchParams, signal?: AbortSignal) =>
      fetchPersonalInstalledTools(params, signal),
    []
  );

  const queryConfig = useMemo(
    () => ({
      staleTime: 30_000,
      refetchInterval: SHADOW_AI_REFRESH_INTERVAL,
      refetchOnWindowFocus: false,
    }),
    []
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Installed AI Tools</CardTitle>
      </CardHeader>
      <CardContent>
        <DataTable<PersonalInstalledToolListItem, PersonalInstalledToolsMeta>
          id={PERSONAL_INSTALLED_TOOLS_TABLE_CONFIG.tableId}
          mode="server"
          className="flex flex-col"
          containerClassName={SHADOW_AI_TABLE_SCROLL_CLASS}
          columns={columns}
          errorBoundaryLabel="Something went wrong in the Installed AI Tools table"
          search={{
            placeholder: 'Search installed AI tools...',
            className: 'w-[260px]',
          }}
          features={{ enableSorting: true, enableRowExpansion: true }}
          rowExpansion={{
            getRowId: getInstalledToolRowId,
            render: row => <ExpandedToolRow row={row} />,
            contentClassName: 'bg-muted/30 p-4',
            rowClassName: 'bg-muted/10',
            keepMounted: false,
            showContainer: false,
            highlightOnExpand: true,
            scrollToExpandedRow: true,
          }}
          queryFn={queryFn}
          getQueryKeysFromParams={getQueryKeysFromParams}
          onMeta={onMeta}
          noDataLabel="No installed AI tools found."
          queryConfig={queryConfig}
        />
      </CardContent>
    </Card>
  );
}

DataTablePersonalInstalledToolsContent.displayName =
  'DataTablePersonalInstalledToolsContent';
